import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { QrCode, CheckCircle2, ScanLine, Camera, CameraOff, Keyboard, Sparkles, Wallet, UserCheck, Ship, Loader2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

const SCAN_REGION_ID = "qr-scan-region";

const PAYMENT_FR = {
  card: "Carte bancaire",
  fineo: "Carte bancaire",
  mobile_money: "Mobile Money",
  cash: "Espèces",
  deposit: "Acompte",
};
const STATUS_FR = {
  pending: "En attente",
  confirmed: "Confirmée",
  arrived: "Embarqué (aller)",
  completed: "Embarqué (aller + retour)",
  cancelled: "Annulée",
};
const DIRECTION_FR = { aller: "Aller", retour: "Retour" };

// Format ISO date (YYYY-MM-DD) to DD/MM/YYYY for display.
function formatDateFR(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}
// Format ISO timestamp to DD/MM/YYYY HH:MM (local).
function formatDateTimeFR(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

export default function StaffScanner() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("camera"); // 'camera' | 'manual'
  const [tokenInput, setTokenInput] = useState("");
  const [result, setResult] = useState(null);
  const [regResult, setRegResult] = useState(null);  // registration pass scan result
  const [todayCrossings, setTodayCrossings] = useState([]);
  const [selectedCrossingId, setSelectedCrossingId] = useState("");
  const [boardingBusy, setBoardingBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [chargeBusy, setChargeBusy] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const scannerRef = useRef(null);
  const processingRef = useRef(false);
  const startingRef = useRef(false);

  // Load activities catalogue once (used for the in-scanner quick-charge buttons)
  useEffect(() => {
    api.get("/staff/activities").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setActivities(list.filter((a) => a.active !== false));
    }).catch((e) => {
      // iter-104 · Log soft errors — the scanner still works without the activity catalogue.
      console.warn("[Scanner] activities catalog fetch failed:", e?.message || e);
    });
  }, []);

  // Stop scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch((e) => {
          // iter-104 · html5-qrcode raises when the video track is already
          // stopped by React StrictMode's double-mount. Not actionable.
          console.debug("[Scanner] stop() during unmount:", e?.message || e);
        });
        scannerRef.current = null;
      }
    };
  }, []);

  const handleScanned = async (decodedText) => {
    if (processingRef.current) return; // ignore subsequent frames
    processingRef.current = true;
    // Detect QR type. The QR can contain:
    //  - {"type":"ticket","token":"…"}        (booking ticket)
    //  - {"type":"wallet","token":"…"}        (activities wallet)
    //  - {"type":"registration","token":"…"}  (self-registration boarding pass)
    //  - {"guest_token":"…",…}                (legacy full JSON payload)
    //  - raw token string                     (manual entry fallback)
    let payload = decodedText.trim();
    let isWallet = false;
    let isRegistration = false;
    let walletToken = null;
    let regToken = null;
    let guestToken = null;
    if (payload.startsWith("{")) {
      try {
        const obj = JSON.parse(payload);
        if (obj.type === "wallet" && obj.token) {
          isWallet = true;
          walletToken = obj.token;
        } else if (obj.type === "registration" && obj.token) {
          isRegistration = true;
          regToken = obj.token;
        } else if (obj.type === "ticket" && obj.token) {
          guestToken = obj.token;
        } else {
          guestToken = obj.guest_token || obj.qr_token || obj.token || null;
        }
      } catch {
        /* not JSON */
      }
    }
    try {
      if (isWallet) {
        await stopCamera();
        toast.success("Carte Activités détectée");
        navigate(`/staff/activites?token=${encodeURIComponent(walletToken)}`);
        return;
      }
      if (isRegistration) {
        await stopCamera();
        await lookupRegistration(regToken);
        return;
      }
      await stopCamera();
      await lookup(guestToken || payload);
    } finally {
      // Allow the next manual scan attempt once UI is in `result` mode.
      // (reset() will clear this on the next scan request.)
    }
  };

  const lookupRegistration = async (token) => {
    setLoading(true);
    setResult(null);
    setRegResult(null);
    try {
      const { data } = await api.get(`/staff/scan/registration/${encodeURIComponent(token)}`);
      setRegResult(data);
      // Load today's scheduled crossings for the dropdown (best-effort)
      try {
        const r = await api.get("/staff/traversees");
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const open = list.filter((c) => c.status !== "terminée" && c.status !== "annulée");
        setTodayCrossings(open);
        if (open.length) setSelectedCrossingId(open[0].id);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pass d'embarquement non reconnu");
    } finally {
      setLoading(false);
    }
  };

  const lookup = async (token) => {
    setLoading(true);
    setResult(null);
    setRegResult(null);
    try {
      let qrToken = (token || "").trim();
      if (qrToken.startsWith("{")) {
        try {
          const obj = JSON.parse(qrToken);
          if (obj.type === "wallet" && obj.token) {
            navigate(`/staff/activites?token=${encodeURIComponent(obj.token)}`);
            return;
          }
          if (obj.type === "registration" && obj.token) {
            await lookupRegistration(obj.token);
            return;
          }
          if (obj.type === "ticket" && obj.token) {
            qrToken = obj.token;
          } else {
            qrToken = obj.guest_token || obj.qr_token || obj.token || qrToken;
          }
        } catch {
          /* keep as-is */
        }
      }
      const { data } = await api.get(`/staff/scan/${qrToken}`).catch(async (e) => {
        // 404 on the booking endpoint? Maybe it's a self-registration token.
        if (e?.response?.status === 404) {
          try {
            const r2 = await api.get(`/staff/scan/registration/${qrToken}`);
            return { data: { __isRegistration: true, payload: r2.data } };
          } catch (e2) {
            throw e; // re-throw original 404 to show "QR non reconnu"
          }
        }
        throw e;
      });
      if (data && data.__isRegistration) {
        // Hydrate registration result flow
        setRegResult(data.payload);
        try {
          const r = await api.get("/staff/traversees");
          const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          const open = list.filter((c) => c.status !== "terminée" && c.status !== "annulée");
          setTodayCrossings(open);
          if (open.length) setSelectedCrossingId(open[0].id);
        } catch { /* ignore */ }
        return;
      }
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "QR code non reconnu");
    } finally {
      setLoading(false);
    }
  };

  const boardRegistration = async () => {
    if (!regResult || !selectedCrossingId || boardingBusy) return;
    setBoardingBusy(true);
    try {
      await api.post(`/staff/traversees/${selectedCrossingId}/board-registration`, {
        registration_id: regResult.id,
      });
      const crossing = todayCrossings.find((c) => c.id === selectedCrossingId);
      const boatLabel = crossing
        ? `${crossing.bateau?.name || "Bateau"} · ${crossing.depart_time}`
        : "la traversée";
      toast.success(`${regResult.first_name} ${regResult.last_name} ajouté à ${boatLabel}`);
      // Refresh the reg result so the boarded_history pill appears
      await lookupRegistration(regResult.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Embarquement impossible");
    } finally {
      setBoardingBusy(false);
    }
  };

  const startCamera = async () => {
    if (startingRef.current) return; // concurrent guard
    startingRef.current = true;
    setCameraError(null);
    // If a previous instance is still around, stop it first.
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch { /* ignore */ }
      scannerRef.current = null;
    }
    try {
      // Wait two animation frames so React has actually committed the <div id> to the DOM.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      // Sanity check: the scan region must exist before instantiating Html5Qrcode.
      const regionEl = document.getElementById(SCAN_REGION_ID);
      if (!regionEl) {
        // Component unmounted or mode switched away — silently abort.
        startingRef.current = false;
        return;
      }
      // Clear any leftover DOM children inside the region (a previous stopped instance
      // may have left an injected <video> / <canvas> behind).
      regionEl.innerHTML = "";
      const instance = new Html5Qrcode(SCAN_REGION_ID);
      scannerRef.current = instance;
      await instance.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
        (decodedText) => handleScanned(decodedText),
        () => {},
      );
      setCameraOn(true);
    } catch (e) {
      setCameraError(
        e?.message?.includes("permission") || e?.name === "NotAllowedError"
          ? "Autorisation caméra refusée. Activez l'accès dans votre navigateur."
          : (e?.message || "Caméra indisponible sur cet appareil."),
      );
      setCameraOn(false);
      scannerRef.current = null;
    } finally {
      startingRef.current = false;
    }
  };

  const stopCamera = async () => {
    if (scannerRef.current) {
      const instance = scannerRef.current;
      scannerRef.current = null;
      // Wrap stop/clear in a timeout so a hung native getUserMedia release on
      // Safari iOS can't block the UI for more than 800ms.
      const timeout = new Promise((resolve) => setTimeout(resolve, 800));
      try {
        await Promise.race([
          (async () => {
            try { await instance.stop(); } catch { /* ignore */ }
            try { await instance.clear(); } catch { /* ignore */ }
          })(),
          timeout,
        ]);
      } catch {
        /* swallow */
      }
    }
    setCameraOn(false);
  };

  const refreshResult = async () => {
    if (!result?.qr_token) return;
    try {
      const { data } = await api.get(`/staff/scan/${result.qr_token}`);
      setResult(data);
    } catch { /* ignore */ }
  };

  const chargeActivity = async (activity) => {
    if (!result?.qr_token || chargeBusy) return;
    setChargeBusy(true);
    try {
      const { data } = await api.post(`/staff/scan/${result.qr_token}/charge`, {
        activity_id: activity.id,
        quantity: 1,
      });
      toast.success(`${activity.name_fr} ajouté · ${data.total_charged.toLocaleString("fr-FR")} FCFA`);
      await refreshResult();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Charge impossible");
    } finally {
      setChargeBusy(false);
    }
  };

  const chargeCustom = async () => {
    if (!result?.qr_token || chargeBusy) return;
    const amount = parseInt(customAmount, 10);
    const label = customLabel.trim();
    if (!label || !amount || amount <= 0) {
      toast.error("Renseignez un libellé et un montant > 0");
      return;
    }
    setChargeBusy(true);
    try {
      await api.post(`/staff/scan/${result.qr_token}/charge`, { label, amount, quantity: 1 });
      toast.success(`${label} ajouté · ${amount.toLocaleString("fr-FR")} FCFA`);
      setCustomLabel("");
      setCustomAmount("");
      await refreshResult();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Charge impossible");
    } finally {
      setChargeBusy(false);
    }
  };

  // Embarkation modal state
  const [checkinModal, setCheckinModal] = useState(null); // null | { direction, plannedBoatTime }
  const [boats, setBoats] = useState([]);
  const [scheduledTraversees, setScheduledTraversees] = useState([]);
  const [traverseesFallback, setTraverseesFallback] = useState(false);  // for the current direction
  const [selectedTraverseeId, setSelectedTraverseeId] = useState("");
  const [loadingTraversees, setLoadingTraversees] = useState(false);
  const [overrideBoatTime, setOverrideBoatTime] = useState("");
  const [overrideBoatId, setOverrideBoatId] = useState("");
  const [skipperName, setSkipperName] = useState("");
  const [recentSkippers, setRecentSkippers] = useState([]);
  const [checkinBusy, setCheckinBusy] = useState(false);

  // Load boats catalogue once (used as fallback dropdown)
  useEffect(() => {
    api.get("/staff/bateaux").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setBoats(list.filter((b) => b.status !== "indisponible"));
    }).catch((e) => {
      console.warn("[Scanner] boats fetch failed:", e?.message || e);
    });
    // Load recent skipper names (shared, last-used first)
    api.get("/staff/skippers").then((r) => {
      const list = Array.isArray(r.data?.items) ? r.data.items : [];
      setRecentSkippers(list.map((s) => s.name).filter(Boolean));
    }).catch((e) => {
      console.warn("[Scanner] skippers fetch failed:", e?.message || e);
    });
  }, []);

  // iter-79 — parse a depart_time string ("HH:MM" or "HHhMM") to minutes since midnight.
  const parseDepartMinutes = (s) => {
    if (!s || typeof s !== "string") return null;
    const m = s.match(/^(\d{1,2})[:hH](\d{2})$/);
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  };

  const openCheckinModal = async () => {
    if (!result) return;
    const direction = result.next_direction;
    if (!direction) return;
    const planned = direction === "aller"
      ? result.boat_time
      : (result.return_boat_time || result.boat_time);

    // Reset selections, then fetch today's scheduled traversées matching the
    // ticket's direction. Only status="programmé" — exclude en_cours / terminée
    // / annulée so the operator cannot accidentally board onto a closed boat.
    setOverrideBoatTime("");
    setOverrideBoatId("");
    setSelectedTraverseeId("");
    setSkipperName("");
    setScheduledTraversees([]);
    setCheckinModal({ direction, planned });
    setLoadingTraversees(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.get(
        `/staff/traversees?date=${today}&direction=${direction}&status=programm%C3%A9`,
      );
      const list = Array.isArray(data) ? data : (data?.items || []);
      const sorted = [...list].sort((a, b) => (a.depart_time || "").localeCompare(b.depart_time || ""));

      // iter-79 — Auto-select the NEXT UPCOMING boat. We accept a small
      // 15-minute grace window before the current time so a boat that just
      // pulled out is still selectable (typical last-minute passenger).
      // Only boats at the very next departure time are surfaced; any later
      // departure remains available via the "Saisir manuellement" fallback.
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      const GRACE_MINUTES = 15;
      const upcoming = sorted.filter((t) => {
        const dt = parseDepartMinutes(t.depart_time);
        return dt != null && dt >= nowMinutes - GRACE_MINUTES;
      });

      // Group by the earliest upcoming depart_time. If nothing matches the
      // upcoming window, fall back to the full list so operators can still
      // pick something (edge case: all scheduled boats already departed).
      let next = upcoming;
      let usedFallback = false;
      if (next.length === 0 && sorted.length > 0) {
        next = sorted;
        usedFallback = true;
      }
      const earliest = next[0]?.depart_time;
      const nextGroup = next.filter((t) => t.depart_time === earliest);
      setScheduledTraversees(nextGroup);
      setTraverseesFallback(usedFallback);

      if (nextGroup.length > 0) {
        // Auto-select the single next boat (or the first of a same-time group).
        const pick = nextGroup[0];
        setSelectedTraverseeId(pick.id);
        setOverrideBoatTime(pick.depart_time || "");
        setOverrideBoatId(pick.bateau_id || "");
        setSkipperName(pick.skipper_name || "");
      }
    } catch {
      // Network or permission issue — modal still opens, operator can use the
      // manual fallback fields below.
    } finally {
      setLoadingTraversees(false);
    }
  };

  // Keep the override fields in sync when the operator picks a different
  // scheduled traversée from the list.
  const selectTraversee = (t) => {
    setSelectedTraverseeId(t.id);
    setOverrideBoatTime(t.depart_time || "");
    setOverrideBoatId(t.bateau_id || "");
    setSkipperName(t.skipper_name || skipperName);
  };

  const submitCheckin = async () => {
    if (!result || !result.qr_token) return;
    setCheckinBusy(true);
    try {
      const payload = {};
      const planned = checkinModal?.planned || "";
      if (overrideBoatTime && overrideBoatTime !== planned) payload.boat_time = overrideBoatTime;
      if (overrideBoatId) payload.boat_id = overrideBoatId;
      const skipper = skipperName.trim();
      if (skipper) payload.skipper_name = skipper;
      const { data } = await api.post(`/staff/scan/${result.qr_token}/checkin`, payload);
      const dirLabel = DIRECTION_FR[data.direction] || data.direction;
      toast.success(
        `Embarquement ${dirLabel.toLowerCase()} enregistré${data.boat_label ? ` · ${data.boat_label}` : ""}${data.skipper_name ? ` · Skipper ${data.skipper_name}` : ""}`,
      );
      const newScan = {
        direction: data.direction,
        scanned_at: data.scanned_at,
        staff_email: data.staff_email,
        staff_name: data.staff_name,
        boat_time: data.boat_time,
        boat_id: data.boat_id,
        boat_name: data.boat_name,
        boat_date: data.boat_date,
        boat_label: data.boat_label,
        overridden: data.overridden,
        skipper_name: data.skipper_name,
      };
      setResult({
        ...result,
        scans: [...(result.scans || []), newScan],
        scan_count: data.scan_count,
        next_direction: data.next_direction,
        fully_used: data.fully_used,
        status: data.booking_status,
      });
      // Promote the skipper to the top of the recent list (de-dup, max 30)
      if (skipper) {
        setRecentSkippers((prev) => [skipper, ...prev.filter((n) => n.toLowerCase() !== skipper.toLowerCase())].slice(0, 30));
      }
      setCheckinModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'embarquement");
    } finally {
      setCheckinBusy(false);
    }
  };

  const reset = () => {
    // iter-79 — Synchronous state reset. The useEffect below detects that
    // `result`/`regResult` cleared and sequences a clean stop → start so the
    // camera reliably comes back within ~200ms (no more black screen).
    processingRef.current = false;
    setTokenInput("");
    setResult(null);
    setRegResult(null);
    setSelectedCrossingId("");
  };

  // iter-79 — Camera lifecycle. All start/stop calls flow through here so we
  // never race an in-flight release against a fresh start. When the operator
  // clicks "Scanner un autre QR" (reset() clears `result`), this effect fully
  // awaits the previous stop, waits 150ms so the browser actually releases the
  // MediaStream (mandatory on Safari iOS to avoid the black-screen bug), then
  // starts a fresh instance.
  useEffect(() => {
    let cancelled = false;
    const wantsCamera = mode === "camera" && !result && !regResult;

    const run = async () => {
      // Always stop first — cheap no-op if nothing is running.
      await stopCamera();
      if (cancelled) return;
      if (!wantsCamera) return;
      // Give the browser one tick to release the previous MediaStream.
      await new Promise((r) => setTimeout(r, 150));
      if (cancelled) return;
      processingRef.current = false;
      await startCamera();
    };
    run();
    return () => { cancelled = true; };
  }, [mode, result, regResult]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="staff-scanner">
      <h1 className="font-display-serif text-2xl sm:text-3xl md:text-4xl text-[#0A0A0A] mb-2 flex items-center gap-3">
        <QrCode size={26} className="text-[#B8922A]" />
        Scanner QR Code
      </h1>
      <p className="text-sm text-[#0A0A0A]/55 mb-6">
        Scannez avec la caméra de l'appareil, ou saisissez manuellement le token.
      </p>

      {/* Mode selector */}
      {!result && !regResult && (
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setMode("camera")}
            className={`flex-1 px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] border transition-all inline-flex items-center justify-center gap-2 ${
              mode === "camera"
                ? "bg-[#B8922A] text-white border-[#B8922A]"
                : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#B8922A]"
            }`}
            data-testid="scanner-mode-camera"
          >
            <Camera size={13} /> Caméra
          </button>
          <button
            onClick={() => setMode("manual")}
            className={`flex-1 px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] border transition-all inline-flex items-center justify-center gap-2 ${
              mode === "manual"
                ? "bg-[#B8922A] text-white border-[#B8922A]"
                : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#B8922A]"
            }`}
            data-testid="scanner-mode-manual"
          >
            <Keyboard size={13} /> Manuel
          </button>
        </div>
      )}

      {/* Camera mode */}
      {!result && !regResult && mode === "camera" && (
        <div className="bg-white border border-[#B8922A]/30 p-4 sm:p-6" data-testid="scanner-camera-card">
          <div
            id={SCAN_REGION_ID}
            className="w-full max-w-md mx-auto bg-[#0A0A0A] aspect-square overflow-hidden rounded"
            style={{ minHeight: 280 }}
          />
          {cameraError ? (
            <div className="mt-5 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 p-3">
              <CameraOff size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                {cameraError}
                <button onClick={startCamera} className="ml-2 underline">
                  Réessayer
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-5 flex items-center justify-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">
              <Sparkles size={11} className="text-[#B8922A]" />
              {cameraOn ? "Approchez le QR Code de la caméra" : "Démarrage de la caméra…"}
            </div>
          )}
        </div>
      )}

      {/* Manual mode */}
      {!result && !regResult && mode === "manual" && (
        <div className="bg-white border border-[#B8922A]/30 p-6 sm:p-10 text-center" data-testid="scanner-input-card">
          <ScanLine size={42} className="text-[#B8922A]/40 mx-auto mb-5" />
          <p className="text-[0.72rem] text-[#0A0A0A]/55 mb-4">
            Saisissez le code de référence visible sur le billet (ex. <span className="font-mono text-[#0A0A0A]">5DF111909C</span>) ou le token complet.
          </p>
          <input
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && tokenInput && lookup(tokenInput)}
            placeholder="Référence (10 caractères) ou token QR…"
            className="w-full border-b border-[#0A0A0A]/15 px-3 py-2.5 text-center text-base font-mono tracking-wider focus:border-[#B8922A] outline-none"
            data-testid="scanner-token-input"
            autoFocus
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            onClick={() => tokenInput && lookup(tokenInput)}
            disabled={!tokenInput || loading}
            className="btn-gold mt-6 inline-flex items-center gap-3"
            data-testid="scanner-lookup-btn"
          >
            {loading ? "Recherche…" : "Vérifier le QR"}
          </button>
        </div>
      )}

      {/* Registration boarding pass result */}
      {regResult && (
        <div className="bg-white border border-[#B8922A]/40 p-5 sm:p-8" data-testid="scanner-reg-result">
          <div className="flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.28em] text-[#B8922A] mb-1">
            <UserCheck size={12} /> Pass d'embarquement
          </div>
          <h2 className="font-display-serif text-2xl sm:text-3xl text-[#0A0A0A] mb-1">
            {regResult.first_name} {regResult.last_name}
          </h2>
          <p className="text-sm text-[#0A0A0A]/55 mb-4">
            {regResult.nationality || "—"} · Réf. <span className="font-mono">{regResult.ref}</span>
          </p>

          <div className="grid sm:grid-cols-2 gap-3 mb-6 text-[0.8rem] text-[#0A0A0A]/80">
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#B8922A] mb-0.5">Expérience</div>
              <div>{regResult.offer_label || "—"}</div>
            </div>
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#B8922A] mb-0.5">Contact</div>
              <div>{regResult.phone || "—"}</div>
              <div className="text-[0.72rem] text-[#0A0A0A]/55">{regResult.email || ""}</div>
            </div>
          </div>

          {/* Already boarded today? */}
          {regResult.boarded_history && regResult.boarded_history.length > 0 && (
            <div className="mb-5 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[0.78rem]" data-testid="reg-boarded-history">
              <div className="font-medium mb-1 inline-flex items-center gap-1.5">
                <CheckCircle2 size={13} /> Déjà embarqué
              </div>
              {regResult.boarded_history.slice(0, 3).map((b, i) => (
                <div key={i} className="text-emerald-700/90">
                  {formatDateTimeFR(b.boarded_at)}
                  {b.client_name ? ` · ${b.client_name}` : ""}
                </div>
              ))}
            </div>
          )}

          {/* Crossing picker */}
          <div className="border border-[#B8922A]/20 p-4 bg-[#FAF7F2] mb-5">
            <div className="flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.28em] text-[#B8922A] mb-2">
              <Ship size={12} /> Ajouter à une traversée du jour
            </div>
            {todayCrossings.length === 0 ? (
              <div className="text-[0.8rem] text-[#0A0A0A]/55 italic">
                Aucune traversée programmée aujourd'hui. Créez-en une depuis "Départs & embarquement".
              </div>
            ) : (
              <>
                <select
                  value={selectedCrossingId}
                  onChange={(e) => setSelectedCrossingId(e.target.value)}
                  className="w-full bg-white border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
                  data-testid="reg-crossing-select"
                >
                  {todayCrossings.map((c) => {
                    const cap = c.bateau?.capacity || 0;
                    const left = Math.max(0, cap - (c.passenger_count || 0));
                    const dirLabel = c.direction === "retour" ? "Retour" : "Aller";
                    return (
                      <option key={c.id} value={c.id}>
                        {c.bateau?.name || "Bateau"} · {dirLabel} · {c.depart_time} · {left}/{cap} places
                      </option>
                    );
                  })}
                </select>
                <button
                  onClick={boardRegistration}
                  disabled={!selectedCrossingId || boardingBusy}
                  className="btn-gold mt-4 w-full inline-flex items-center justify-center gap-2"
                  data-testid="reg-board-btn"
                >
                  {boardingBusy ? <Loader2 size={14} className="animate-spin" /> : <Ship size={14} />}
                  {boardingBusy ? "Embarquement…" : "Ajouter au bateau"}
                </button>
              </>
            )}
          </div>

          <button
            onClick={reset}
            className="text-[0.65rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 hover:text-[#B8922A] inline-flex items-center gap-2"
            data-testid="reg-rescan-btn"
          >
            ↺ Scanner un autre QR
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white border border-[#B8922A]/40 p-5 sm:p-8" data-testid="scanner-result">
          <div className="text-[0.62rem] uppercase tracking-[0.28em] text-[#B8922A] mb-1">QR Valide</div>
          <h2 className="font-display-serif text-2xl sm:text-3xl text-[#0A0A0A] mb-1">
            {result.guest_surname || result.guest_name ? `${result.guest_surname || ""} ${result.guest_name || ""}`.trim() : "Invité"}
          </h2>
          <p className="text-sm text-[#0A0A0A]/55 mb-1">{result.guest_nationality || "—"}</p>
          {(result.guest_phone || result.guest_email) && (
            <p className="text-[0.72rem] text-[#0A0A0A]/55 mb-6 flex flex-wrap gap-x-3">
              {result.guest_phone && <span>{result.guest_phone}</span>}
              {result.guest_email && <span>· {result.guest_email}</span>}
            </p>
          )}
          {!result.guest_phone && !result.guest_email && <div className="mb-6" />}

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm border-y border-[#0A0A0A]/10 py-5">
            <div>
              <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Offre</dt>
              <dd className="text-[#0A0A0A] font-medium mt-0.5">{result.offer_name || "—"}</dd>
            </div>
            <div>
              <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">
                {result.checkout_date ? "Arrivée" : "Date"}
              </dt>
              <dd className="text-[#0A0A0A] font-medium mt-0.5">{formatDateFR(result.date)}</dd>
            </div>
            {result.checkout_date && (
              <>
                <div>
                  <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Départ</dt>
                  <dd className="text-[#0A0A0A] font-medium mt-0.5">
                    {formatDateFR(result.checkout_date)}
                    {result.nights > 0 && (
                      <span className="text-[0.7rem] text-[#0A0A0A]/55 ml-2">
                        · {result.nights} nuit{result.nights > 1 ? "s" : ""}
                      </span>
                    )}
                  </dd>
                </div>
                {result.room_tier_name && (
                  <div>
                    <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Chambre</dt>
                    <dd className="text-[#0A0A0A] font-medium mt-0.5">
                      {result.room_tier_name}
                      {result.rooms > 1 && <span className="text-[0.7rem] text-[#0A0A0A]/55 ml-2">× {result.rooms}</span>}
                    </dd>
                  </div>
                )}
              </>
            )}
            <div>
              <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">
                {result.checkout_date ? "Bateau aller" : "Bateau"}
              </dt>
              <dd className="text-[#0A0A0A] font-medium mt-0.5">{result.boat_time || "—"}</dd>
            </div>
            {result.return_boat_time && (
              <div>
                <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Bateau retour</dt>
                <dd className="text-[#0A0A0A] font-medium mt-0.5">{result.return_boat_time}</dd>
              </div>
            )}
            <div className="sm:col-span-2">
              <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Composition du groupe</dt>
              <dd className="text-[#0A0A0A] font-medium mt-0.5" data-testid="scanner-composition">
                {/* Prefer the explicit composition stored on this QR ticket */}
                {result.composition ? (
                  <>
                    <span>{result.composition.adults || result.adults} adulte{(result.composition.adults || result.adults) > 1 ? "s" : ""}</span>
                    {(result.composition.children_paid ?? 0) > 0 && (
                      <span> · {result.composition.children_paid} enfant{result.composition.children_paid > 1 ? "s" : ""} 6–12</span>
                    )}
                    {(result.composition.children_free ?? 0) > 0 && (
                      <span> · {result.composition.children_free} enfant{result.composition.children_free > 1 ? "s" : ""} &lt;6 (gratuit)</span>
                    )}
                  </>
                ) : (
                  <>
                    <span>{result.adults} adulte{result.adults > 1 ? "s" : ""}</span>
                    {result.children > 0 && <span> · {result.children} enfant{result.children > 1 ? "s" : ""}</span>}
                  </>
                )}
                {result.booking_code && (
                  <span className="ml-2 text-[0.7rem] text-[#0A0A0A]/55 font-mono">
                    · Code {result.booking_code}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Paiement</dt>
              <dd className="text-[#0A0A0A] font-medium mt-0.5">
                {PAYMENT_FR[result.payment_method] || result.payment_method || "—"}
                {result.total_amount > 0 && ` · ${result.total_amount.toLocaleString("fr-FR")} FCFA`}
              </dd>
            </div>
            {result.balance_due > 0 && (
              <div className="sm:col-span-2 bg-amber-50 border border-amber-200 px-3 py-2 -mx-2">
                <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-amber-700">
                  Solde à régler à l'arrivée{result.deposit_pct ? ` · acompte ${result.deposit_pct}% versé` : ""}
                </dt>
                <dd className="text-amber-900 font-display-serif text-lg mt-0.5">
                  {result.balance_due.toLocaleString("fr-FR")} FCFA
                </dd>
              </div>
            )}
            <div>
              <dt className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Statut</dt>
              <dd className="text-[#0A0A0A] font-medium mt-0.5">{STATUS_FR[result.status] || result.status || "—"}</dd>
            </div>
          </dl>

          {result.special_requests && (
            <div className="mt-5">
              <div className="text-[0.62rem] uppercase tracking-[0.22em] text-[#B8922A] mb-1">Demandes spéciales</div>
              <div className="text-sm text-[#0A0A0A]/80">{result.special_requests}</div>
            </div>
          )}

          {/* Scans history */}
          {(result.scans && result.scans.length > 0) && (
            <div className="mt-6 bg-[#FAFAF7] border border-[#0A0A0A]/8 px-4 py-3" data-testid="scanner-scans-history">
              <div className="text-[0.62rem] uppercase tracking-[0.22em] text-[#B8922A] mb-2">
                Embarquements enregistrés · {result.scans.length}/2
              </div>
              <ul className="space-y-1.5">
                {result.scans.map((s, i) => (
                  <li
                    key={i}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-[#0A0A0A] gap-1"
                    data-testid={`scan-history-${i}`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CheckCircle2 size={13} className="text-green-600" />
                      <span className="font-medium">{DIRECTION_FR[s.direction] || s.direction}</span>
                      {s.boat_label && (
                        <span className="text-[0.62rem] uppercase tracking-[0.18em] text-[#B8922A] border border-[#B8922A]/30 bg-[#B8922A]/5 px-1.5 py-0.5 rounded-sm">
                          {s.boat_label}
                        </span>
                      )}
                      {s.overridden && (
                        <span className="text-[0.6rem] uppercase tracking-[0.18em] text-amber-700 border border-amber-300 bg-amber-50 px-1.5 py-0.5 rounded-sm" title={`Initialement prévu : ${s.planned_boat_time || "—"}`}>
                          Modifié
                        </span>
                      )}
                    </span>
                    <span className="text-[0.72rem] text-[#0A0A0A]/55 sm:text-right">
                      {formatDateTimeFR(s.scanned_at)}
                      {(s.staff_name || s.staff_email) && (
                        <span className="ml-2">· par {s.staff_name || s.staff_email}</span>
                      )}
                      {s.skipper_name && (
                        <span className="ml-2">· Skipper {s.skipper_name}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Embarkation CTA — opens a modal to confirm the actual boat taken */}
          {result.next_direction ? (
            <button
              onClick={openCheckinModal}
              className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white py-4 text-sm uppercase tracking-[0.22em] inline-flex items-center justify-center gap-3 transition-colors"
              data-testid={`scanner-checkin-${result.next_direction}-btn`}
            >
              <CheckCircle2 size={18} />
              {result.next_direction === "aller"
                ? "Embarquement aller"
                : "Embarquement retour"}
            </button>
          ) : (
            <div
              className="mt-6 w-full bg-green-50 border border-green-200 text-green-700 py-4 text-sm uppercase tracking-[0.22em] flex items-center justify-center gap-3"
              data-testid="scanner-fully-used"
            >
              <CheckCircle2 size={18} /> QR entièrement utilisé (aller + retour)
            </div>
          )}

          {result.wallet_token && (
            <div className="mt-6 border border-[#B8922A]/30 bg-[#FAF6E8]/40 p-4 sm:p-5" data-testid="scanner-charge-panel">
              <div className="flex items-baseline justify-between mb-3 gap-2">
                <div>
                  <div className="text-[0.62rem] uppercase tracking-[0.28em] text-[#B8922A]">
                    Activités & consommations
                  </div>
                  <p className="text-[0.72rem] text-[#0A0A0A]/55 mt-0.5">
                    Imputées à ce participant — réglées à la sortie.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Total</div>
                  <div className="font-display-serif text-lg text-[#B8922A]" data-testid="participant-total">
                    {(result.participant_total_charged || 0).toLocaleString("fr-FR")} FCFA
                  </div>
                </div>
              </div>

              {/* Quick activity buttons (catalogue) */}
              {activities.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4" data-testid="scanner-activity-grid">
                  {activities.slice(0, 9).map((a) => (
                    <button
                      key={a.id}
                      onClick={() => chargeActivity(a)}
                      disabled={chargeBusy}
                      className="text-left px-3 py-2.5 bg-white border border-[#0A0A0A]/10 hover:border-[#B8922A] hover:bg-[#B8922A]/5 transition-all disabled:opacity-40"
                      data-testid={`scanner-charge-${a.id}`}
                    >
                      <div className="text-[0.7rem] font-medium text-[#0A0A0A] truncate">{a.name_fr}</div>
                      <div className="text-[0.65rem] text-[#B8922A]">{a.price.toLocaleString("fr-FR")} FCFA</div>
                    </button>
                  ))}
                </div>
              )}

              {/* Custom amount */}
              <div className="flex flex-col sm:flex-row gap-2" data-testid="scanner-custom-charge">
                <input
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  placeholder="Libellé (ex. Boisson, Cocktail…)"
                  className="flex-1 border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none bg-white"
                  data-testid="scanner-custom-label"
                />
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Montant FCFA"
                  className="w-full sm:w-36 border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none bg-white"
                  data-testid="scanner-custom-amount"
                />
                <button
                  onClick={chargeCustom}
                  disabled={chargeBusy || !customLabel || !customAmount}
                  className="px-4 py-2 text-[0.65rem] uppercase tracking-[0.22em] bg-[#B8922A] text-white hover:bg-[#a37e1f] transition-all disabled:opacity-40"
                  data-testid="scanner-custom-add"
                >
                  Ajouter
                </button>
              </div>

              {/* Per-participant history */}
              {result.participant_charges && result.participant_charges.length > 0 && (
                <div className="mt-4 border-t border-[#B8922A]/15 pt-3" data-testid="scanner-participant-history">
                  <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 mb-2">
                    Historique ({result.participant_charges.length})
                  </div>
                  <ul className="space-y-1.5">
                    {result.participant_charges.map((c) => (
                      <li key={c.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-[0.78rem] text-[#0A0A0A] gap-0.5">
                        <span className="truncate">
                          {c.label}
                          {c.quantity > 1 && <span className="text-[#0A0A0A]/55"> × {c.quantity}</span>}
                          {c.created_by && (
                            <span className="text-[0.65rem] text-[#0A0A0A]/55 ml-2">· par {c.created_by}</span>
                          )}
                        </span>
                        <span className="text-[#0A0A0A] font-medium sm:ml-3 flex-shrink-0">
                          {c.amount.toLocaleString("fr-FR")} FCFA
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {result.wallet_token && (
            <button
              onClick={() => navigate(`/staff/activites?token=${encodeURIComponent(result.wallet_token)}`)}
              className="mt-3 w-full bg-white hover:bg-[#FAFAF7] border border-[#B8922A]/40 text-[#B8922A] py-3 text-sm uppercase tracking-[0.22em] inline-flex items-center justify-center gap-3 transition-colors"
              data-testid="scanner-open-wallet-btn"
            >
              <Wallet size={14} /> Voir la carte complète
            </button>
          )}

          <button
            onClick={reset}
            className="mt-5 w-full bg-[#FAFAF7] hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] border border-[#0A0A0A]/15 hover:border-[#0A0A0A] py-3 text-[0.7rem] uppercase tracking-[0.22em] inline-flex items-center justify-center gap-3 transition-colors"
            data-testid="scanner-new-scan-btn"
          >
            <ScanLine size={14} /> Scanner un autre QR
          </button>
        </div>
      )}

      {/* Embarkation modal — confirm the boat actually taken */}
      {checkinModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !checkinBusy && setCheckinModal(null)} data-testid="checkin-modal">
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-[#0A0A0A]/8">
              <div>
                <div className="text-[0.62rem] uppercase tracking-[0.28em] text-[#B8922A]">
                  Embarquement {DIRECTION_FR[checkinModal.direction]?.toLowerCase()}
                </div>
                <div className="font-display-serif text-lg text-[#0A0A0A] mt-0.5">
                  {result.guest_surname} {result.guest_name}
                </div>
              </div>
            </div>
            <div className="p-5 sm:p-6 space-y-4">
              <div className="bg-[#FAFAF7] border border-[#0A0A0A]/8 p-3 text-sm">
                <div className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">Bateau prévu</div>
                <div className="font-medium text-[#0A0A0A] mt-0.5">{checkinModal.planned || "—"}</div>
              </div>

              {/* iter-79 — Prochaine traversée disponible.
                  When only 1 upcoming boat exists we show it as a plain card
                  (already auto-selected). When 2+ boats depart at the SAME
                  next time, we render a compact side-by-side selector. */}
              <div>
                <label className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 mb-1.5 block">
                  {scheduledTraversees.length > 1
                    ? "Plusieurs bateaux à la même heure — choisissez"
                    : "Prochaine traversée"}
                </label>
                {traverseesFallback && scheduledTraversees.length > 0 && (
                  <div className="mb-2 p-2.5 bg-amber-50 border border-amber-200 text-[0.7rem] text-amber-900" data-testid="checkin-past-boat-warning">
                    Aucun bateau à venir aujourd&apos;hui — celui affiché est <strong>déjà parti</strong>.
                    Confirmez uniquement si vous embarquez un passager en retard, ou utilisez « Saisir manuellement ».
                  </div>
                )}
                {loadingTraversees ? (
                  <div className="p-3 bg-[#FAFAF7] text-[0.78rem] text-[#0A0A0A]/55 inline-flex items-center gap-2">
                    <Loader2 size={12} className="animate-spin" /> Chargement…
                  </div>
                ) : scheduledTraversees.length === 0 ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 text-[0.78rem] text-amber-800" data-testid="checkin-no-traversee">
                    Aucune traversée <strong>{checkinModal.direction}</strong> à venir aujourd&apos;hui.
                    Utilisez « Saisir manuellement » ou demandez à la logistique d&apos;en programmer une.
                  </div>
                ) : (
                  <div className={scheduledTraversees.length > 1 ? "grid grid-cols-2 gap-1.5" : "space-y-1.5"} data-testid="checkin-traversee-list">
                    {scheduledTraversees.map((t) => {
                      const isActive = selectedTraverseeId === t.id;
                      const cap = t.bateau?.capacity || 0;
                      const taken = t.passenger_count || 0;
                      const remain = Math.max(0, cap - taken);
                      const full = cap > 0 && remain === 0;
                      return (
                        <button
                          key={t.id}
                          onClick={() => !full && selectTraversee(t)}
                          disabled={full}
                          className={`w-full text-left p-3 border transition-all ${
                            isActive
                              ? "border-[#B8922A] bg-[#FAF7F2] shadow-[0_0_0_1px_#B8922A_inset]"
                              : full
                                ? "border-[#0A0A0A]/10 bg-[#FAFAF7] text-[#0A0A0A]/40 cursor-not-allowed"
                                : "border-[#0A0A0A]/12 bg-white hover:border-[#0A0A0A]/35"
                          }`}
                          data-testid={`checkin-traversee-${t.id.slice(0, 8)}`}
                        >
                          <div className="flex items-baseline justify-between gap-3">
                            <div>
                              <span className="font-display-serif text-base text-[#0A0A0A]">{t.depart_time}</span>
                              <span className="text-[0.7rem] text-[#0A0A0A]/55 ml-2">
                                {t.bateau?.name || "Bateau ?"}
                              </span>
                            </div>
                            <span className={`text-[0.65rem] uppercase tracking-wide ${full ? "text-red-600" : "text-emerald-700"}`}>
                              {full ? "Complet" : `${remain}/${cap || "?"} places`}
                            </span>
                          </div>
                          {t.skipper_name && (
                            <div className="text-[0.65rem] text-[#0A0A0A]/55 mt-0.5">Skipper · {t.skipper_name}</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <details className="text-[0.7rem] text-[#0A0A0A]/55" data-testid="checkin-override-toggle">
                <summary className="cursor-pointer hover:text-[#0A0A0A]">
                  Saisir manuellement (cas exceptionnel)
                </summary>
                <div className="mt-2 space-y-2">
                  <input
                    value={overrideBoatTime}
                    onChange={(e) => setOverrideBoatTime(e.target.value)}
                    placeholder="Heure (ex. 11H30)"
                    className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none text-sm bg-white"
                    data-testid="checkin-boat-time-input"
                  />
                  <select
                    value={overrideBoatId}
                    onChange={(e) => setOverrideBoatId(e.target.value)}
                    className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none text-sm bg-white"
                    data-testid="checkin-boat-select"
                  >
                    <option value="">— Bateau non précisé —</option>
                    {boats.map((b) => (
                      <option key={b.id} value={b.id}>{b.name} {b.capacity ? `(${b.capacity} places)` : ""}</option>
                    ))}
                  </select>
                </div>
              </details>
              <div>
                <label className="text-[0.62rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 mb-1.5 block">
                  Nom du skipper
                </label>
                <input
                  list="recent-skippers"
                  value={skipperName}
                  onChange={(e) => setSkipperName(e.target.value)}
                  placeholder="Skipper en charge du bateau"
                  className="w-full px-3 py-2 border border-[#0A0A0A]/15 focus:border-[#B8922A] outline-none text-sm bg-white"
                  data-testid="checkin-skipper-input"
                  autoComplete="off"
                />
                <datalist id="recent-skippers">
                  {recentSkippers.map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
                {recentSkippers.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="checkin-skipper-chips">
                    {recentSkippers.slice(0, 6).map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setSkipperName(n)}
                        className={`px-2.5 py-1 text-[0.68rem] border transition-all ${
                          skipperName === n
                            ? "bg-[#B8922A] text-white border-[#B8922A]"
                            : "bg-white text-[#0A0A0A]/75 border-[#0A0A0A]/15 hover:border-[#B8922A]"
                        }`}
                        data-testid={`checkin-skipper-chip-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {overrideBoatTime && checkinModal.planned && overrideBoatTime !== checkinModal.planned && (
                <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-[0.72rem] text-amber-900">
                  Ce client embarque sur un bateau différent ({overrideBoatTime}) du bateau prévu ({checkinModal.planned}).
                </div>
              )}
            </div>
            <div className="border-t border-[#0A0A0A]/8 p-4 sm:p-5 flex justify-end gap-2">
              <button
                onClick={() => setCheckinModal(null)}
                disabled={checkinBusy}
                className="px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] border border-[#0A0A0A]/15 text-[#0A0A0A]/65 hover:border-[#0A0A0A] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
                data-testid="checkin-cancel"
              >
                Annuler
              </button>
              <button
                onClick={submitCheckin}
                disabled={checkinBusy || !overrideBoatTime}
                className="px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] bg-green-600 text-white hover:bg-green-700 transition-colors inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="checkin-confirm"
              >
                <CheckCircle2 size={13} /> {checkinBusy ? "…" : "Confirmer l'embarquement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
